// Analysis screen (Screen 5) — PSD §10.2. 3 tabs: Sensitivity, Scenarios, What-If.
//
// Stage 6e /polish: the single tab content panel used to be a .card, which
// meant Analysis had the same surface as every other screen. Drop the
// container — the tab-bar already anchors the active tab visually, and the
// panels below carry their own internal structure. Less repetition, one
// less undifferentiated card on the page.

import { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { useActiveValuation, useStore } from '../store/selectors';
import { EmptyState } from '../components/common/EmptyState';
import { LoadDemoButton } from '../components/common/LoadDemoButton';
import { SensitivityGrid } from '../components/analysis/SensitivityGrid';
import { ScenarioCompare } from '../components/analysis/ScenarioCompare';
import { WhatIfPanel } from '../components/analysis/WhatIfPanel';

type Tab = 'sensitivity' | 'scenarios' | 'whatif';

export function Analysis() {
  const [tab, setTab] = useState<Tab>('sensitivity');
  const active = useActiveValuation();
  const navigate = useStore((s) => s.navigate);

  return (
    <div>
      <h1 className="screen-title">Analysis</h1>
      <div className="tab-bar">
        <button className={`tab ${tab === 'sensitivity' ? 'active' : ''}`} onClick={() => setTab('sensitivity')}>
          Sensitivity
        </button>
        <button className={`tab ${tab === 'scenarios' ? 'active' : ''}`} onClick={() => setTab('scenarios')}>
          Scenarios
        </button>
        <button className={`tab ${tab === 'whatif' ? 'active' : ''}`} onClick={() => setTab('whatif')}>
          What-if
        </button>
      </div>
      {!active?.inputs ? (
        <EmptyState
          icon={<FlaskConical size={32} strokeWidth={1.5} />}
          message="Run a valuation first."
          hint="Sensitivity, scenarios, and what-if recompute a converged result's inputs across volatility, equity-value, and discount ranges — there's no active valuation to analyze yet."
          action={
            <>
              <LoadDemoButton />
              <button type="button" className="btn btn-secondary" onClick={() => navigate('builder')}>
                Go to Builder
              </button>
            </>
          }
        />
      ) : (
        // key={tab} remounts the panel on switch so the entrance animation
        // replays — a short transform/opacity reveal that softens the cut.
        // (Stage 6f /animate)
        <div className="panel-open tab-panel" key={tab}>
          {tab === 'sensitivity' && <SensitivityGrid inputs={active.inputs} />}
          {tab === 'scenarios' && <ScenarioCompare inputs={active.inputs} />}
          {tab === 'whatif' && <WhatIfPanel inputs={active.inputs} />}
        </div>
      )}
    </div>
  );
}
